import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gallery } from './api';
import { formatDate, formatBytes } from './format';
import './Portal.css';

const ACCEPT = 'image/jpeg,image/png,image/webp';

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const fileInput = useRef(null);
  const uploading = useRef(false);
  const queueRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const result = await gallery.list(true);
      setItems(result.items);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // --- Uploading -----------------------------------------------------------

  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;

    const accepted = [];
    const videos = [];
    const rejected = [];
    picked.forEach((file) => {
      if (ACCEPT.split(',').indexOf(file.type) !== -1) {
        accepted.push(file);
      } else if (/^video\//.test(file.type) || /\.(mp4|mov|m4v|avi|webm|mkv|wmv|flv|3gp|mpe?g)$/i.test(file.name)) {
        // Called out separately — telling someone their video "is not a JPG"
        // reads as a file-format problem they could fix by converting it.
        videos.push(file.name);
      } else {
        rejected.push(file.name);
      }
    });

    const problems = [];
    if (videos.length) {
      problems.push(
        videos.join(', ') +
          (videos.length === 1 ? ' is a video. ' : ' are videos. ') +
          'This gallery is photos only — upload a screenshot or a still frame instead.'
      );
    }
    if (rejected.length) {
      problems.push(
        rejected.join(', ') +
          (rejected.length === 1 ? ' is not a JPG, PNG or WebP.' : ' are not JPG, PNG or WebP files.') +
          ' If these came from an iPhone, set Settings › Camera › Formats to "Most Compatible".'
      );
    }
    setError(problems.length ? problems.join(' ') : null);

    setQueue((current) =>
      current.concat(
        accepted.map((file) => ({
          key: file.name + '-' + file.size + '-' + file.lastModified + '-' + Math.random().toString(36).slice(2, 8),
          file,
          preview: URL.createObjectURL(file),
          progress: 0,
          status: 'waiting',
          message: null,
        }))
      )
    );
  };

  const patch = useCallback((key, changes) => {
    setQueue((current) =>
      current.map((entry) => (entry.key === key ? Object.assign({}, entry, changes) : entry))
    );
  }, []);

  /**
   * Uploads run strictly one at a time — a handful of phone photos fired off in
   * parallel over mobile data tends to time out, and sequential gives an honest
   * per-file progress bar.
   *
   * The pump reads the queue through a ref rather than through the effect
   * closure: every progress tick writes to state, so anything keyed on the
   * render-time `queue` value would be re-created mid-upload.
   */
  const pump = useCallback(() => {
    if (uploading.current) return;
    const next = queueRef.current.find((entry) => entry.status === 'waiting');
    if (!next) return;

    uploading.current = true;
    patch(next.key, { status: 'uploading', progress: 0 });

    gallery
      .upload(next.file, { title: stripExtension(next.file.name) }, (percent) =>
        patch(next.key, { progress: percent })
      )
      .then((response) => {
        // Released before the state write, so the effect below can start the
        // next file as soon as React re-renders.
        uploading.current = false;
        setItems((current) => [response.item].concat(current));
        patch(next.key, { status: 'done', progress: 100, message: 'Added to the gallery' });
        // Clear finished rows shortly after, so the queue does not grow forever.
        setTimeout(() => {
          URL.revokeObjectURL(next.preview);
          setQueue((current) => current.filter((entry) => entry.key !== next.key));
        }, 2500);
      })
      .catch((err) => {
        uploading.current = false;
        patch(next.key, { status: 'error', message: err.message });
      });
  }, [patch]);

  useEffect(() => {
    queueRef.current = queue;
    pump();
  }, [queue, pump]);

  // Release any previews still held when the owner navigates away mid-upload.
  useEffect(
    () => () => {
      queueRef.current.forEach((entry) => URL.revokeObjectURL(entry.preview));
    },
    []
  );

  const retry = (key) => {
    setQueue((current) =>
      current.map((entry) =>
        entry.key === key ? Object.assign({}, entry, { status: 'waiting', message: null, progress: 0 }) : entry
      )
    );
  };

  const dismiss = (key) => {
    setQueue((current) => {
      const entry = current.find((row) => row.key === key);
      if (entry) URL.revokeObjectURL(entry.preview);
      return current.filter((row) => row.key !== key);
    });
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    addFiles(event.dataTransfer.files);
  };

  // --- Editing -------------------------------------------------------------

  const saveItem = async (id, changes) => {
    setError(null);
    try {
      const response = await gallery.update(id, changes);
      setItems((current) => current.map((item) => (item.id === id ? response.item : item)));
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const togglePublished = (item) => saveItem(item.id, { published: !item.published });

  const removeItem = async (id) => {
    // Two-step confirm that reverts on its own — an accidental first tap on a
    // phone should not leave a live "Really delete?" button sitting there.
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId((current) => (current === id ? null : current)), 5000);
      return;
    }
    setError(null);
    try {
      await gallery.remove(id);
      setItems((current) => current.filter((item) => item.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err.message);
      setConfirmDeleteId(null);
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;

    const next = items.slice();
    const moved = next.splice(index, 1)[0];
    next.splice(target, 0, moved);
    setItems(next);

    try {
      await gallery.reorder(next.map((item) => item.id));
    } catch (err) {
      setError(err.message);
      load();
    }
  };

  // --- Render --------------------------------------------------------------

  const visibleCount = items.filter((item) => item.published).length;

  return (
    <div>
      <section className="pt-section">
        <div
          className={'pt-drop' + (dragging ? ' is-dragging' : '')}
          onClick={() => fileInput.current && fileInput.current.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (fileInput.current) fileInput.current.click();
            }
          }}
        >
          <div className="pt-drop-icon">
            <span role="img" aria-label="Camera">
              📷
            </span>
          </div>
          <div className="pt-drop-title">Add photos of your work</div>
          <div className="pt-drop-sub">
            Tap to choose photos, or drag them here. JPG, PNG or WebP, up to 12 MB each.
            <br />
            Photos only — videos cannot be uploaded. They go straight onto the website gallery.
          </div>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {queue.length > 0 && (
          <div className="pt-queue">
            {queue.map((entry) => (
              <div className="pt-queue-item" key={entry.key}>
                <img className="pt-queue-thumb" src={entry.preview} alt="" />
                <div className="pt-queue-body">
                  <div className="pt-queue-name">{entry.file.name}</div>
                  <div
                    className={
                      'pt-queue-status' +
                      (entry.status === 'error' ? ' is-error' : entry.status === 'done' ? ' is-done' : '')
                    }
                  >
                    {entry.status === 'waiting' && 'Waiting…'}
                    {entry.status === 'uploading' && 'Uploading ' + entry.progress + '%'}
                    {entry.status === 'done' && (entry.message || 'Done')}
                    {entry.status === 'error' && entry.message}
                  </div>
                  {entry.status === 'uploading' && (
                    <div className="pt-progress">
                      <div className="pt-progress-bar" style={{ width: entry.progress + '%' }} />
                    </div>
                  )}
                </div>
                {entry.status === 'error' && (
                  <div className="pt-actions">
                    <button className="pt-btn pt-btn-ghost pt-btn-sm" type="button" onClick={() => retry(entry.key)}>
                      Retry
                    </button>
                    <button className="pt-btn pt-btn-ghost pt-btn-sm" type="button" onClick={() => dismiss(entry.key)}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="pt-notice pt-notice-error" style={{ marginTop: 14 }}>
            {error}
          </div>
        )}
      </section>

      <section className="pt-section">
        <div className="pt-section-head">
          <h2 className="pt-section-title">
            Your gallery{items.length > 0 ? ' — ' + visibleCount + ' live, ' + items.length + ' total' : ''}
          </h2>
          <button className="pt-btn pt-btn-ghost pt-btn-sm" type="button" onClick={load}>
            Refresh
          </button>
        </div>

        {loading && <div className="pt-loading">Loading your photos…</div>}

        {!loading && items.length === 0 && (
          <div className="pt-empty">
            <div className="pt-empty-title">No photos yet</div>
            Add your first photo above and it will appear on the website straight away.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="pt-photos">
            {items.map((item, index) => (
              <article className={'pt-photo' + (item.published ? '' : ' is-hidden-item')} key={item.id}>
                <img className="pt-photo-img" src={item.thumb_url} alt={item.title} loading="lazy" />

                <div className="pt-photo-body">
                  {editingId === item.id ? (
                    <PhotoEditor item={item} onCancel={() => setEditingId(null)} onSave={saveItem} />
                  ) : (
                    <>
                      <h3 className="pt-photo-title">{item.title || 'Untitled'}</h3>
                      {item.caption && <p className="pt-photo-caption">{item.caption}</p>}
                      <div className="pt-photo-meta">
                        {item.published ? 'Live on the site' : 'Hidden'} · {item.width}×{item.height} ·{' '}
                        {formatBytes(item.size)} · {formatDate(item.uploaded_at)}
                      </div>

                      <div className="pt-photo-actions">
                        <button
                          className="pt-btn pt-btn-ghost pt-btn-sm"
                          type="button"
                          onClick={() => setEditingId(item.id)}
                        >
                          Edit
                        </button>
                        <button
                          className="pt-btn pt-btn-ghost pt-btn-sm"
                          type="button"
                          onClick={() => togglePublished(item)}
                        >
                          {item.published ? 'Hide' : 'Show'}
                        </button>
                        <button
                          className="pt-btn pt-btn-ghost pt-btn-sm"
                          type="button"
                          onClick={() => move(index, -1)}
                          disabled={index === 0}
                          title="Move earlier in the gallery"
                        >
                          ↑
                        </button>
                        <button
                          className="pt-btn pt-btn-ghost pt-btn-sm"
                          type="button"
                          onClick={() => move(index, 1)}
                          disabled={index === items.length - 1}
                          title="Move later in the gallery"
                        >
                          ↓
                        </button>
                        <button
                          className="pt-btn pt-btn-danger pt-btn-sm"
                          type="button"
                          onClick={() => removeItem(item.id)}
                        >
                          {confirmDeleteId === item.id ? 'Really delete?' : 'Delete'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PhotoEditor({ item, onCancel, onSave }) {
  const [title, setTitle] = useState(item.title);
  const [caption, setCaption] = useState(item.caption);
  const [category, setCategory] = useState(item.category);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(item.id, { title, caption, category });
    setSaving(false);
  };

  return (
    <div className="pt-photo-edit">
      <input
        className="pt-input"
        type="text"
        value={title}
        placeholder="Title, e.g. Kitchen remodel"
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
      />
      <textarea
        className="pt-textarea"
        value={caption}
        placeholder="Short description (optional)"
        onChange={(e) => setCaption(e.target.value)}
      />
      <input
        className="pt-input"
        type="text"
        value={category}
        placeholder="Category, e.g. Flooring (optional)"
        onChange={(e) => setCategory(e.target.value)}
      />
      <div className="pt-actions">
        <button className="pt-btn pt-btn-primary pt-btn-sm" type="button" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="pt-btn pt-btn-ghost pt-btn-sm" type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function stripExtension(filename) {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(0, dot) : filename;
}
